/**
 * Save path for a single-name inline rename (pipeline card title, record
 * header title). Extracted from `InlineCardTitle.tsx` so both surfaces share
 * one implementation.
 *
 * CRITICAL — single combined optimistic event, never two events at the same
 * seq: `createRecordStore.ts`'s seq guard skips any event whose `seq` is
 * `<=` the entity's current stored seq (see `crmObjectStore.ts:85-93`). Two
 * events built at the same seq (one for the real property, one for a
 * derived property like `ahoy_display_name`) are NOT both applied — the
 * store accepts the first and silently drops the second as a duplicate.
 *
 * The caller MUST fold every optimistic property (the real edit plus any
 * derived property, e.g. `ahoy_display_name`) into `optimisticProperties`
 * and pass it as ONE event to `applyOptimisticBatch`. Only the real,
 * user-editable property (`wireProperties`) is submitted over the wire —
 * derived properties are recomputed server-side (e.g. by `FormulaCascade`)
 * and their WebSocket echo reconciles the optimistic value later.
 *
 * This can't be expressed with `useUpdateCrmProperty` or
 * `submitEventsOptimistic`: both tie the optimistic store write 1:1 to the
 * submitted wire batch, so covering a derived property optimistically would
 * mean submitting it too — one call for the real property then a second for
 * the derived one ships the second at `last_seq + 2` (seq math computed from
 * a store already bumped by the first), which the backend rejects as a
 * non-retryable 400, not a retryable 409. Instead this builds ONE local
 * event covering every optimistic property at the SAME seq, applies it via
 * a single `applyOptimisticBatch` call (one rollback context — a denied
 * rename can't strand a phantom value on shared org data), and submits only
 * the real property's event over the wire.
 */

import { createEventsClient } from '@/lib/crm/core/EventsClient';
import type { BaseEvent } from '@/lib/crm/core/types';
import { getObjectSeq } from '@/lib/crm/store/crmObjectStore';
import {
  applyOptimisticBatch,
  rollbackOptimisticBatch,
} from '@/lib/crm/store/optimisticBatch';
import type { CrmObject } from '@/types/crm';

export interface SaveNameRenameInput {
  /** The entity being renamed. */
  object: CrmObject;
  orgId: number;
  userId: string | number;
  /** Properties actually submitted to the server. */
  wireProperties: Record<string, unknown>;
  /**
   * Properties applied to the local store optimistically — MUST include
   * every key in `wireProperties` plus any derived property (e.g.
   * `ahoy_display_name`) that should read updated immediately rather than
   * waiting for the server cascade's WebSocket echo.
   */
  optimisticProperties: Record<string, unknown>;
}

/** Single-name rename save path: one optimistic event, one wire event. */
export async function saveNameRename({
  object,
  orgId,
  userId,
  wireProperties,
  optimisticProperties,
}: SaveNameRenameInput): Promise<void> {
  const seq =
    Math.max(getObjectSeq(object.entity_id) ?? 0, object.last_event_seq) + 1;
  const occurredAt = new Date().toISOString();

  // Local-only: never submitted. Covers every optimistic property (real edit
  // + any derived property) at the SAME seq so the store's seq guard applies
  // all of them together instead of dropping a second same-seq event.
  const optimisticEvent: BaseEvent = {
    entity_id: object.entity_id,
    entity_type_name: object.entity_type_name,
    event_type_name: 'ahoy_property_updated',
    seq,
    idempotency_key: crypto.randomUUID(),
    occurred_at: occurredAt,
    payload: { properties: optimisticProperties },
    source_type_name: 'ahoy_user',
    source_id: String(userId),
  };
  // The only event actually submitted to the server.
  const wireEvent: BaseEvent = {
    entity_id: object.entity_id,
    entity_type_name: object.entity_type_name,
    event_type_name: 'ahoy_property_updated',
    seq,
    idempotency_key: crypto.randomUUID(),
    occurred_at: occurredAt,
    payload: { properties: wireProperties },
    source_type_name: 'ahoy_user',
    source_id: String(userId),
  };

  const ctx = applyOptimisticBatch({ events: [optimisticEvent] }, orgId);
  try {
    await createEventsClient(orgId).submitEvents([wireEvent]);
  } catch (err) {
    rollbackOptimisticBatch(ctx);
    throw err;
  }
}
